/**
 * Brand siblings (tier 2) for the "You may also like" section.
 *
 * Tiers 1 (same-formula alternates) and 3 (same category) are rendered
 * server-side by sections/product-recommendations.liquid. This module fills
 * the gap between them with the same brand at other strengths / pack sizes,
 * e.g. AVSAR 5/80 -> AVSAR 10/160.
 *
 * It reuses the one lazy request the section already made for Shopify's
 * recommendations, so the page's request count is unchanged, and it only
 * fires when the section is close to the viewport.
 */
(() => {
  const SELECTOR = 'product-recommendations[data-dawa-relevance="true"]';

  /** @type {Map<string, Promise<string>>} */
  const cache = new Map();

  function fetchBrandSiblings(query) {
    const url = `/search?q=${encodeURIComponent(query)}&options%5Bprefix%5D=last&section_id=brand-siblings`;

    let pending = cache.get(url);
    if (!pending) {
      pending = fetch(url)
        .then((res) => (res.ok ? res.text() : ''))
        .catch(() => '');
      cache.set(url, pending);
    }
    return pending;
  }

  function collectExistingIds(list, excludeId) {
    const ids = new Set();
    if (excludeId) ids.add(String(excludeId));
    list.querySelectorAll('[data-product-id]').forEach((el) => {
      ids.add(String(el.dataset.productId));
    });
    return ids;
  }

  async function hydrate(root) {
    const list = root.querySelector('[data-dawa-list]');
    const slot = root.querySelector('[data-brand-slot]');
    const { brandQuery, maxProducts, excludeId } = root.dataset;

    if (!list || !slot || !brandQuery) return;

    const max = Number(maxProducts) || 4;

    const html = await fetchBrandSiblings(brandQuery);
    if (!html) {
      slot.remove();
      return;
    }

    const parsed = document.createElement('div');
    parsed.innerHTML = html;

    const candidates = parsed.querySelectorAll('[data-brand-siblings] .resource-list__item');
    if (!candidates.length) {
      slot.remove();
      return;
    }

    const seen = collectExistingIds(list, excludeId);
    const fragment = document.createDocumentFragment();
    let added = 0;

    for (const card of candidates) {
      const id = String(card.dataset.productId);
      if (seen.has(id)) continue;
      seen.add(id);
      card.setAttribute('data-tier', 'brand');
      fragment.appendChild(card);
      added += 1;
    }

    if (added) slot.parentNode.insertBefore(fragment, slot);
    slot.remove();

    // Priority order is alternates -> brand -> category, so trim any overflow
    // from the end, which drops the lowest-priority (category) items first.
    const items = Array.from(list.querySelectorAll('.resource-list__item'));
    for (let i = max; i < items.length; i += 1) {
      items[i].remove();
    }

    if (items.length) list.setAttribute('data-has-recommendations', 'true');
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer.unobserve(entry.target);
        hydrate(entry.target);
      }
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  function init() {
    document.querySelectorAll(SELECTOR).forEach((el) => observer.observe(el));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
